import {
  AdvancedSearchConfig,
  AdvancedSearchFieldConfig,
  AdvancedSearchFormField,
  booleanFieldComparator,
  BooleanFieldSearchConfig,
  CurrencyFieldSearchConfig,
  EnumFieldSearchConfig,
  FieldConfigTypes,
  findFieldByKey,
  isBooleanFieldConfig,
  isCurrencyFieldConfig,
  isEnumFieldConfig,
  isMultilingualFieldConfig,
  isNumberFieldConfig,
  isStringFieldConfig,
  isUnknownFormField,
  jsonFieldComparator,
  MultilingualFieldSearchConfig,
  numberFieldComparator,
  NumberFieldSearchConfig,
  stringFieldComparator,
  StringFieldSearchConfig,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import { Trash2Icon } from "lucide-react";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { FormControl, FormField, HeadlessFormItem } from "../ui/form";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AdvancedQueryFieldSelector } from "./AdvancedQueryFieldSelector";

const getDefaultFieldValues = (
  key: string,
  config: AdvancedSearchConfig,
): AdvancedSearchFormField => {
  const fieldSelected = findFieldByKey(key, config);

  if (fieldSelected == undefined) return { type: "unknown" };

  return { ...fieldSelected } as AdvancedSearchFormField; // safe, since AdvancedSearchFieldConfig is a subset of AdvancedSearchFormField
};

type InputProps<FormFieldType extends FieldConfigTypes = FieldConfigTypes> = {
  config: AdvancedSearchFieldConfig<FormFieldType>;
  fieldRef: string;
};

export type ComparatorOption = string | { value: string; label: string };
export type ComparatorValue<T> = T extends { value: string } ? T["value"] : T;

function extractLabelFromComparatorOption(option: ComparatorOption) {
  if (typeof option === "string") return option;
  return option.label;
}

function extractValuefromComparatorOption(option: ComparatorOption) {
  if (typeof option === "string") return option;
  return option.value;
}

const ComparatorField = ({
  options,
  fieldRef,
}: {
  fieldRef: string;
  options: readonly ComparatorOption[];
}) => {
  const form = useFormContext();

  const { t } = useTranslation();

  return (
    <FormField
      name={`${fieldRef}.comparator`}
      {...form}
      rules={{ required: true }}
      render={({ field }) => (
        <HeadlessFormItem>
          <FormControl>
            <Select
              value={field.value || ""}
              onValueChange={(v) => {
                field.onChange(v);
                form.trigger(`${fieldRef}.value`);
              }}
            >
              <SelectTrigger className="rounded-none w-64 min-w-0 truncate">
                <SelectValue placeholder={""} />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem
                    key={extractValuefromComparatorOption(o)}
                    value={extractValuefromComparatorOption(o)}
                  >
                    {t(extractLabelFromComparatorOption(o))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
        </HeadlessFormItem>
      )}
    />
  );
};

const BooleanInputField = ({
  fieldRef,
  config: _c,
}: InputProps<BooleanFieldSearchConfig>) => {
  const form = useFormContext();

  const { t } = useTranslation();

  return (
    <>
      <ComparatorField fieldRef={fieldRef} options={booleanFieldComparator} />
      <FormField
        name={`${fieldRef}.value`}
        {...form}
        defaultValue={undefined}
        rules={{ validate: (v) => v != undefined }}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <Select
                value={field.value?.toString() || ""}
                onValueChange={(v) => field.onChange(v === "true")}
              >
                <SelectTrigger className="rounded-l-none w-full">
                  <SelectValue
                    placeholder={t("advancedSearchBooleanPlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t("true")}</SelectItem>
                  <SelectItem value="false">{t("false")}</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    </>
  );
};

const StringInputField = ({
  fieldRef,
  config: _c,
}: InputProps<StringFieldSearchConfig>) => {
  const form = useFormContext();

  const comparatorValue = form.watch(`${fieldRef}.comparator`) as string;

  const inputTextRequired = useMemo(
    () => ["empty", "notEmpty"].includes(comparatorValue ?? "") === false,
    [comparatorValue],
  );

  const { t } = useTranslation();

  return (
    <>
      <ComparatorField fieldRef={fieldRef} options={stringFieldComparator} />
      {inputTextRequired && (
        <FormField
          name={`${fieldRef}.value`}
          defaultValue={""}
          rules={{
            validate: (v) =>
              inputTextRequired ? v != undefined && v !== "" : true,
          }}
          {...form}
          render={({ field }) => (
            <HeadlessFormItem>
              <FormControl>
                <Input
                  className="w-full rounded-l-none"
                  placeholder={t("advancedSearchStringPlaceholder")}
                  {...field}
                />
              </FormControl>
            </HeadlessFormItem>
          )}
        />
      )}
    </>
  );
};

const MultilingualInputField = ({
  fieldRef,
  config: _c,
}: InputProps<MultilingualFieldSearchConfig>) => {
  const form = useFormContext();
  const { t } = useTranslation();

  return (
    <>
      <ComparatorField fieldRef={fieldRef} options={jsonFieldComparator} />
      <FormField
        name={`${fieldRef}.value`}
        defaultValue={""}
        rules={{
          validate: (v) => v != undefined && v !== "",
        }}
        {...form}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <Input
                className="w-full rounded-l-none"
                placeholder={t("advancedSearchStringPlaceholder")}
                {...field}
              />
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    </>
  );
};

const EnumInputField = ({
  fieldRef,
  config,
}: InputProps<EnumFieldSearchConfig>) => {
  const form = useFormContext();
  return (
    <>
      <ComparatorField fieldRef={fieldRef} options={booleanFieldComparator} />
      <FormField
        rules={{ required: true }}
        name={`${fieldRef}.value`}
        defaultValue={config.options[0].name}
        {...form}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <Select value={field.value || ""} onValueChange={field.onChange}>
                <SelectTrigger className="rounded-l-none truncate w-full min-w-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config.options.map((o) => (
                    <SelectItem key={o.name} value={o.name}>
                      {o.label ? o.label : `${o.name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    </>
  );
};

const NumberInputField = ({
  fieldRef,
  config: _c,
}: InputProps<NumberFieldSearchConfig | CurrencyFieldSearchConfig>) => {
  const form = useFormContext();

  const { t } = useTranslation();

  return (
    <>
      <ComparatorField fieldRef={fieldRef} options={numberFieldComparator} />
      <FormField
        name={`${fieldRef}.value`}
        rules={{ required: true }}
        {...form}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <Input
                {...field}
                placeholder={t("advancedSearchNumberPlaceholder")}
              />
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    </>
  );
};

export const AdvancedSearchFormRow = ({
  config,
  fieldRef,
  rowIndex,
  onDeleteRow,
}: {
  rowIndex: number;
  fieldRef: string;
  config: AdvancedSearchConfig;
  onDeleteRow?: (rowIndex: number) => void;
}) => {
  const form = useFormContext();

  const { watch, getValues } = form;

  const { t } = useTranslation();

  const fieldKey = watch(`${fieldRef}.key`) as string; // we need to re-render evey time the value changes

  const selectedField = useMemo(
    () => findFieldByKey(fieldKey, config),
    [fieldKey, config],
  );

  const restOfInput = useMemo(() => {
    const defaultReturn = <></>;

    const fieldVal = getValues(`${fieldRef}`);

    if (fieldVal == undefined || isUnknownFormField(fieldVal))
      return defaultReturn;

    const relatedConfig = findFieldByKey(fieldKey, config);

    if (relatedConfig == undefined) return defaultReturn;

    if (isBooleanFieldConfig(relatedConfig))
      return <BooleanInputField {...{ config: relatedConfig, fieldRef }} />;
    if (isEnumFieldConfig(relatedConfig))
      return <EnumInputField {...{ config: relatedConfig, fieldRef }} />;
    if (
      isNumberFieldConfig(relatedConfig) ||
      isCurrencyFieldConfig(relatedConfig)
    )
      return <NumberInputField {...{ config: relatedConfig, fieldRef }} />;
    if (isStringFieldConfig(relatedConfig))
      return <StringInputField {...{ config: relatedConfig, fieldRef }} />;
    if (isMultilingualFieldConfig(relatedConfig))
      return (
        <MultilingualInputField {...{ config: relatedConfig, fieldRef }} />
      );
  }, [config, fieldKey, getValues, fieldRef]);

  return (
    <div className="flex flex-row justify-between w-full gap-x-2 overflow-auto">
      {rowIndex >= 1 && (
        <FormField
          name="searchMode"
          defaultValue={"and"}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={field.onChange}
              disabled={rowIndex !== 1}
            >
              <SelectTrigger className="w-16">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="and">{t("AND")}</SelectItem>
                <SelectItem value="or">{t("OR")}</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      )}
      <div className="flex flex-row gap-0 w-full overflow-auto">
        <FormField
          name={`${fieldRef}.key`}
          rules={{ required: true, validate: (key) => key !== "unknown" }}
          {...form}
          render={() => (
            <HeadlessFormItem>
              <FormControl>
                <AdvancedQueryFieldSelector
                  className={
                    getValues(`${fieldRef}.type`) !== "unknown"
                      ? "rounded-r-none"
                      : undefined
                  }
                  config={config}
                  onSelect={(value) => {
                    form.setValue(
                      fieldRef,
                      getDefaultFieldValues(value.key, config),
                    );
                    form.setValue(`${fieldRef}.comparator`, "");
                    form.trigger(`${fieldRef}.value`);
                    form.trigger(`${fieldRef}.comparator`);
                  }}
                  value={selectedField}
                />
              </FormControl>
            </HeadlessFormItem>
          )}
        />
        {restOfInput}
      </div>
      {rowIndex > 0 && (
        <Button
          className="w-fit shrink-0"
          variant={"ghost"}
          onClick={() => onDeleteRow && onDeleteRow(rowIndex)}
        >
          <Trash2Icon className="stroke-gray-600" />
        </Button>
      )}
    </div>
  );
};
